import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { ENTITY_TYPE_LABELS } from '@/lib/deleteFlow';
import { Check, X, RefreshCw, Search, Eye, Inbox, History, ChevronDown, ChevronRight, Shield, MessageSquare, ExternalLink, AlertTriangle, CheckCircle2, XCircle, Undo2 } from 'lucide-react';

const STATUS_INFO = {
  pending_ca: { label: 'Awaiting Company Admin', tone: 'bg-amber-50 text-amber-700' },
  pending_sa: { label: 'Awaiting Super Admin', tone: 'bg-blue-50 text-blue-700' },
  approved_executed: { label: 'Approved & Deleted', tone: 'bg-emerald-50 text-emerald-700' },
  rejected_ca: { label: 'Rejected by CA', tone: 'bg-rose-50 text-rose-700' },
  rejected_sa: { label: 'Rejected by SA', tone: 'bg-rose-50 text-rose-700' },
  sent_back_to_user: { label: 'Sent back to requester', tone: 'bg-slate-100 text-slate-700' },
  sent_back_to_ca: { label: 'Sent back to CA', tone: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Cancelled', tone: 'bg-slate-100 text-slate-600' },
};

export default function Requests() {
  return (
    <AppShell title="Delete Requests" subtitle="Approval workflow: User → Company Admin → Super Admin.">
      <RequestsContent />
    </AppShell>
  );
}

function RequestsContent() {
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();
  const [tab, setTab] = useState('awaiting');
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ status: 'all', search: '' });
  const [counts, setCounts] = useState({ awaiting_me: 0, pending_ca: 0, pending_sa: 0 });
  // Phase 9.9 — SA Attention comments
  const [attentionList, setAttentionList] = useState([]);
  const [attentionCount, setAttentionCount] = useState(0);

  const load = async () => {
    try {
      const params = {};
      if (tab === 'awaiting') params.role_scope = 'awaiting_me';
      else if (tab === 'mine') params.role_scope = 'my_requests';
      else if (tab === 'history') {
        // All for SA/CA, mine for user; just include closed
      }
      if (filter.status !== 'all' && tab === 'history') params.status = filter.status;
      // Skip delete-requests fetch when on the SA attention tab
      if (tab === 'sa_attention') { setList([]); return; }
      const { data } = await api.get('/delete-requests/', { params });
      let docs = data;
      if (tab === 'history') {
        docs = docs.filter((r) => !['pending_ca', 'pending_sa', 'sent_back_to_user', 'sent_back_to_ca'].includes(r.status));
      }
      if (filter.search) {
        const q = filter.search.toLowerCase();
        docs = docs.filter((d) => (d.entity_label || '').toLowerCase().includes(q) || (d.entity_type || '').includes(q) || (d.requested_by_email || '').toLowerCase().includes(q));
      }
      setList(docs);
    } catch (e) { toast.error(extractError(e, 'Load failed')); }
  };
  const loadCounts = async () => {
    try { const { data } = await api.get('/delete-requests/counts'); setCounts(data); } catch {}
  };

  // Phase 9.9 / Wave 3.44 — Load SA Attention comments (Super Admin only).
  // The backend now filters out items the current SA has already
  // acknowledged so the count reflects reality without client-side trim.
  const loadAttention = async () => {
    if (!isSuperAdmin) return;
    try {
      const { data } = await api.get('/orders/sa-attention/pending');
      setAttentionList(data || []);
      setAttentionCount((data || []).length);
    } catch (e) {
      // Soft-fail to keep the rest of the page working
      // eslint-disable-next-line no-console
      console.warn('Failed to load SA attention comments', e);
    }
  };

  useEffect(() => { load(); loadCounts(); /* eslint-disable-next-line */ }, [tab, filter.status]); // eslint-disable-line
  // Always keep the SA attention badge fresh for Super Admins
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isSuperAdmin) loadAttention(); }, [isSuperAdmin, tab]);

  // Wave 3.44 — Re-fetch the SA attention list whenever the window regains
  // focus. This catches the common round-trip where the SA clicks an
  // attention card → reads the comment on the Order Detail page (which
  // auto-acknowledges it) → comes back to /requests. Without this the
  // count would stay stale until manual refresh.
  useEffect(() => {
    if (!isSuperAdmin) return;
    const onFocus = () => loadAttention();
    const onVisible = () => { if (document.visibilityState === 'visible') loadAttention(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  // Apply local search filter to attention list (search by order_no / author / body)
  const filteredAttention = useMemo(() => {
    if (!filter.search) return attentionList;
    const q = filter.search.toLowerCase();
    return attentionList.filter((c) =>
      (c.order_no || '').toLowerCase().includes(q) ||
      (c.created_by_name || '').toLowerCase().includes(q) ||
      (c.created_by_email || '').toLowerCase().includes(q) ||
      (c.body || '').toLowerCase().includes(q) ||
      (c.comment_no || '').toLowerCase().includes(q)
    );
  }, [attentionList, filter.search]);

  return (
    <div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[var(--divider)]">
          <TabsTrigger value="awaiting" data-testid="requests-tab-awaiting">
            <Inbox size={14} className="mr-1.5" />
            Awaiting You
            {counts.awaiting_me > 0 && (
              <Badge className="ml-2 bg-rose-50 text-rose-700 text-[10px]" data-testid="requests-awaiting-count">{counts.awaiting_me}</Badge>
            )}
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="sa_attention" data-testid="requests-tab-sa-attention">
              <AlertTriangle size={14} className="mr-1.5" />
              Attention Requests
              {attentionCount > 0 && (
                <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]" data-testid="requests-sa-attention-count">{attentionCount}</Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="mine" data-testid="requests-tab-mine">My Requests</TabsTrigger>
          <TabsTrigger value="history" data-testid="requests-tab-history">
            <History size={14} className="mr-1.5" /> All / History
          </TabsTrigger>
        </TabsList>

        <div className="sticky-filter-bar mt-4 flex flex-wrap gap-2 items-end justify-between rounded-lg px-3" data-testid="requests-filter-bar">
          <div className="flex gap-2 items-end">
            <div className="relative">
              <Label className="text-[11px] text-slate-500">Search</Label>
              <Search size={12} className="absolute left-3 bottom-2.5 text-slate-400" />
              <Input
                className="pl-7 h-9 w-56"
                placeholder={tab === 'sa_attention' ? 'order no, author, or text' : 'entity, requester, or type'}
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                data-testid="requests-search-input"
              />
            </div>
            {tab === 'history' && (
              <div>
                <Label className="text-[11px] text-slate-500">Status</Label>
                <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
                  <SelectTrigger className="w-56 h-9" data-testid="requests-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.entries(STATUS_INFO).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { load(); loadCounts(); loadAttention(); }}
            data-testid="requests-refresh-button"
          >
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>

        <TabsContent value="awaiting" className="mt-4">
          <RequestList list={list} reload={() => { load(); loadCounts(); }} canAct />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="sa_attention" className="mt-4">
            <SAAttentionList list={filteredAttention} reload={loadAttention} />
          </TabsContent>
        )}
        <TabsContent value="mine" className="mt-4">
          <RequestList list={list} reload={() => { load(); loadCounts(); }} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <RequestList list={list} reload={() => { load(); loadCounts(); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RequestList({ list, reload, canAct }) {
  const [expanded, setExpanded] = useState({});
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();
  const [selected, setSelected] = useState(() => new Set());

  // A request is actionable in bulk only when the current admin can approve /
  // reject it right now (mirrors the per-card action gating below).
  const isActionable = useCallback((r) => {
    if (!canAct) return false;
    const canCAAct = (r.status === 'pending_ca' || r.status === 'sent_back_to_ca') && (isCompanyAdmin || isSuperAdmin);
    const canSAAct = r.status === 'pending_sa' && isSuperAdmin;
    return canCAAct || canSAAct;
  }, [canAct, isCompanyAdmin, isSuperAdmin]);

  const actionableIds = useMemo(() => list.filter(isActionable).map((r) => r.id), [list, isActionable]);

  // Keep the selection in sync with what's visible (e.g. after a reload the
  // acted-on rows disappear, so drop them from the selection set).
  useEffect(() => {
    setSelected((prev) => {
      const allowed = new Set(actionableIds);
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [actionableIds]);

  const toggleSelect = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = actionableIds.length > 0 && actionableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(actionableIds));
  const clearSelection = () => setSelected(new Set());

  if (list.length === 0) {
    return (
      <Card className="p-10 text-center text-sm text-slate-500" data-testid="requests-empty">
        No requests to show here.
      </Card>
    );
  }
  const showBulk = canAct && actionableIds.length > 0;
  return (
    <div className="space-y-2" data-testid="requests-list">
      {showBulk && (
        <BulkActionBar
          actionableCount={actionableIds.length}
          selectedCount={selected.size}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleAll={toggleAll}
          selectedIds={[...selected]}
          reload={reload}
          clearSelection={clearSelection}
        />
      )}
      {list.map((r) => (
        <RequestCard
          key={r.id}
          r={r}
          expanded={!!expanded[r.id]}
          toggle={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}
          reload={reload}
          canAct={canAct}
          currentUserId={user?.id}
          selectable={showBulk && isActionable(r)}
          selected={selected.has(r.id)}
          onToggleSelect={() => toggleSelect(r.id)}
        />
      ))}
    </div>
  );
}

// Sticky bulk-action toolbar: select-all + count + Approve/Reject/Send-back in bulk.
function BulkActionBar({ actionableCount, selectedCount, allSelected, someSelected, onToggleAll, selectedIds, reload, clearSelection }) {
  const [dialog, setDialog] = useState(null); // 'approve' | 'reject' | 'send_back' | null
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const requireNote = dialog === 'reject' || dialog === 'send_back';

  const runBulk = async () => {
    if (!selectedIds.length || !dialog) return;
    setBusy(true);
    try {
      const { data } = await api.post('/delete-requests/bulk-action', {
        request_ids: selectedIds, action: dialog, note,
      });
      const okN = data.succeeded_count || 0;
      const failN = data.failed_count || 0;
      const verb = dialog === 'approve' ? 'Approved' : dialog === 'reject' ? 'Rejected' : 'Sent back';
      if (failN === 0) toast.success(`${verb} ${okN} request${okN === 1 ? '' : 's'}`);
      else if (okN === 0) toast.error(`All ${failN} failed${data.failed?.[0]?.error ? ` — ${data.failed[0].error}` : ''}`);
      else toast.warning(`${okN} ${verb.toLowerCase()}, ${failN} failed${data.failed?.[0]?.error ? ` — ${data.failed[0].error}` : ''}`);
      setDialog(null); setNote('');
      clearSelection();
      reload();
    } catch (e) {
      toast.error(extractError(e, 'Bulk action failed'));
    } finally { setBusy(false); }
  };

  const dialogTitle = dialog === 'approve' ? 'Approve' : dialog === 'reject' ? 'Reject' : 'Send back';

  return (
    <Card className="p-3 sticky top-2 z-20 border-[var(--divider)] shadow-sm bg-white/95 backdrop-blur" data-testid="requests-bulk-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
            onCheckedChange={onToggleAll}
            data-testid="requests-select-all"
            aria-label="Select all actionable requests"
          />
          <span className="text-sm text-slate-700" data-testid="requests-selected-count">
            {selectedCount > 0
              ? `${selectedCount} selected`
              : `Select all (${actionableCount})`}
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            disabled={selectedCount === 0}
            onClick={() => setDialog('approve')}
            data-testid="requests-bulk-approve"
          >
            <CheckCircle2 size={14} className="mr-1.5" /> Approve selected
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            disabled={selectedCount === 0}
            onClick={() => setDialog('send_back')}
            data-testid="requests-bulk-send-back"
          >
            <Undo2 size={14} className="mr-1.5" /> Send back selected
          </Button>
          <Button
            size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
            disabled={selectedCount === 0}
            onClick={() => setDialog('reject')}
            data-testid="requests-bulk-reject"
          >
            <XCircle size={14} className="mr-1.5" /> Reject selected
          </Button>
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) { setDialog(null); setNote(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogTitle} {selectedIds.length} request{selectedIds.length === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription>
              {dialog === 'approve'
                ? 'Each selected request will proceed through the approval workflow. Requests awaiting final approval will be deleted.'
                : dialog === 'send_back'
                  ? 'Each selected request is returned to the previous step. Add a note so the recipient knows what to change.'
                  : 'A note is required so requesters understand why their requests were rejected.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Note {requireNote && <span className="text-rose-600">*</span>}</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context (visible to the requesters)"
              data-testid="requests-bulk-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialog(null); setNote(''); }}>Cancel</Button>
            <Button
              onClick={runBulk}
              disabled={busy || (requireNote && !note.trim())}
              data-testid="requests-bulk-confirm"
              className={dialog === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : dialog === 'send_back' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}
            >
              {busy ? 'Working…' : `Confirm ${dialogTitle.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RequestCard({ r, expanded, toggle, reload, canAct, currentUserId, selectable, selected, onToggleSelect }) {
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();
  const info = STATUS_INFO[r.status] || { label: r.status, tone: 'bg-slate-100' };
  const isOwn = currentUserId === r.requested_by;

  const canCAAct = (r.status === 'pending_ca' || r.status === 'sent_back_to_ca') && (isCompanyAdmin || isSuperAdmin);
  const canSAAct = r.status === 'pending_sa' && isSuperAdmin;
  const canUserAct = r.status === 'sent_back_to_user' && isOwn;

  const action = async (act, note = '') => {
    try {
      await api.post(`/delete-requests/${r.id}/action`, { action: act, note });
      toast.success(`Marked as ${act}`);
      reload();
    } catch (e) {
      toast.error(extractError(e, 'Action failed'));
    }
  };

  return (
    <Card className={`p-4 ${selected ? 'ring-2 ring-[var(--gold-accent)] ring-offset-1' : ''}`} data-testid={`request-card-${r.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        {selectable && (
          <Checkbox
            checked={!!selected}
            onCheckedChange={onToggleSelect}
            className="mt-1"
            data-testid={`request-select-${r.id}`}
            aria-label={`Select ${r.entity_label}`}
          />
        )}
        <button onClick={toggle} className="text-slate-500 hover:text-slate-900 mt-0.5" data-testid={`request-toggle-${r.id}`}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="text-[10px] capitalize">{(ENTITY_TYPE_LABELS[r.entity_type] || r.entity_type).replace('_', ' ')}</Badge>
            <span className="font-medium text-[var(--ink-navy)] truncate" data-testid={`request-label-${r.id}`}>{r.entity_label}</span>
            <Badge className={`text-[10px] ${info.tone}`} data-testid={`request-status-${r.id}`}>{info.label}</Badge>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Requested by <span className="font-medium text-slate-700">{r.requested_by_email}</span> on {formatDateTime(r.requested_at)}
          </div>
          {r.reason && <div className="text-xs text-slate-600 mt-1">Reason: {r.reason}</div>}
        </div>
        {canAct && (
          <div className="flex flex-wrap gap-2">
            {(canCAAct || canSAAct) && (
              <>
                <ActionButton variant="approve" onConfirm={(note) => action('approve', note)} testid={`request-approve-${r.id}`} />
                <ActionButton variant="send_back" onConfirm={(note) => action('send_back', note)} testid={`request-sendback-${r.id}`} />
                <ActionButton variant="reject" onConfirm={(note) => action('reject', note)} testid={`request-reject-${r.id}`} />
              </>
            )}
            {canUserAct && (
              <>
                <ActionButton variant="resubmit" onConfirm={(note) => action('resubmit', note)} testid={`request-resubmit-${r.id}`} />
                <ActionButton variant="cancel" onConfirm={(note) => action('cancel', note)} testid={`request-cancel-${r.id}`} />
              </>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-[var(--divider)]">
          <div className="text-xs font-medium text-slate-700 mb-2">History</div>
          <div className="space-y-1.5 text-xs text-slate-600" data-testid={`request-history-${r.id}`}>
            {(r.history || []).map((h, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span>
                  <span className="capitalize font-medium">{h.action.replace('_', ' ')}</span>
                  {h.role && <span className="text-slate-400"> · {h.role.replace('_', ' ')}</span>}
                  {' '}by <span className="font-mono">{h.by_email}</span>
                  {h.note && <span className="text-slate-500"> — {h.note}</span>}
                </span>
                <span className="text-slate-400 whitespace-nowrap">{formatDateTime(h.at)}</span>
              </div>
            ))}
          </div>
          <details className="mt-3">
            <summary className="text-[11px] text-slate-500 cursor-pointer">Show entity snapshot</summary>
            <pre className="mt-2 max-h-72 overflow-auto text-[10px] bg-slate-50 p-2 rounded border border-[var(--divider)]">
              {JSON.stringify(r.entity_snapshot, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  );
}

// Phase 9.9 — Super Admin Attention list
function SAAttentionList({ list, reload }) {
  if (!list || list.length === 0) {
    return (
      <Card className="p-10 text-center text-sm text-slate-500" data-testid="sa-attention-empty">
        <div className="flex flex-col items-center gap-2">
          <AlertTriangle size={28} className="text-emerald-500" />
          <div className="font-medium text-slate-700">All caught up</div>
          <div>No comments are awaiting your acknowledgment.</div>
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="sa-attention-list">
      {list.map((c) => (
        <SAAttentionCard key={c.id} c={c} reload={reload} />
      ))}
    </div>
  );
}

function SAAttentionCard({ c, reload }) {
  const navigate = useNavigate();
  const [acking, setAcking] = useState(false);
  const initials = (c.created_by_name || c.created_by_email || '?')
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const role = c.created_by_role;
  const isSA = role === 'super_admin';
  const isCA = role === 'company_admin';
  const open = () => {
    // Navigating to the order detail with hash will trigger the auto-acknowledge
    // logic (see /api/orders/{order_id}/comments) and scroll-to-comment.
    navigate(`/orders/${c.order_id}#comment-${c.id}`);
  };
  // Wave 3.44 — Manual acknowledgement without leaving the Requests page.
  const acknowledge = async (e) => {
    e.stopPropagation();
    if (acking) return;
    setAcking(true);
    try {
      await api.post(`/orders/sa-attention/${c.id}/acknowledge`);
      toast.success(`Acknowledged ${c.comment_no}`);
      if (reload) reload();
    } catch (err) {
      toast.error(extractError(err, 'Acknowledge failed'));
      setAcking(false);
    }
  };
  const body = (c.body || '').trim();
  return (
    <Card
      className="p-4 cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-amber-400"
      onClick={open}
      data-testid={`sa-attention-card-${c.id}`}
    >
      <div className="flex gap-3 items-start">
        <div
          className={`h-9 w-9 shrink-0 rounded-full grid place-items-center text-[11px] font-medium ${
            isSA ? 'bg-violet-100 text-violet-700' : isCA ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
          }`}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-amber-50 text-amber-800 text-[10px]" data-testid={`sa-attention-flag-${c.id}`}>
              <Shield size={9} className="mr-0.5" /> Awaiting Super Admin
            </Badge>
            <span className="text-sm font-medium text-[var(--ink-navy)]" data-testid={`sa-attention-order-${c.id}`}>
              Order {c.order_no}
            </span>
            <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c.comment_no}</span>
            <span className="text-slate-400 text-[10px]">{formatDateTime(c.created_at)}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{c.created_by_name || c.created_by_email}</span>
            {isSA && <span className="ml-1 text-violet-700">· Super Admin</span>}
            {isCA && <span className="ml-1 text-blue-700">· Company Admin</span>}
          </div>
          <div
            className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words line-clamp-3"
            data-testid={`sa-attention-body-${c.id}`}
          >
            {body}
          </div>
        </div>
        <div className="shrink-0 flex flex-col gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); open(); }}
            data-testid={`sa-attention-open-${c.id}`}
          >
            <MessageSquare size={13} className="mr-1.5" /> Open
            <ExternalLink size={11} className="ml-1.5" />
          </Button>
          {/* Wave 3.44 — Manual acknowledgement (idempotent on the server). */}
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={acking}
            onClick={acknowledge}
            data-testid={`sa-attention-ack-${c.id}`}
            title="Mark this attention request as acknowledged. The comment itself stays in place."
          >
            <Check size={13} className="mr-1.5" /> {acking ? 'Acknowledging…' : 'Acknowledge'}
          </Button>
        </div>
      </div>
    </Card>
  );
}


function ActionButton({ variant, onConfirm, testid }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const meta = {
    approve: { label: 'Approve', icon: <Check size={14} className="mr-1" />, cls: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
    reject: { label: 'Reject', icon: <X size={14} className="mr-1" />, cls: 'bg-rose-600 hover:bg-rose-700 text-white' },
    send_back: { label: 'Send back', icon: <RefreshCw size={14} className="mr-1" />, cls: '' },
    resubmit: { label: 'Resubmit', icon: <RefreshCw size={14} className="mr-1" />, cls: 'luxury-button' },
    cancel: { label: 'Cancel request', icon: <X size={14} className="mr-1" />, cls: 'bg-slate-200 hover:bg-slate-300' },
  };
  const m = meta[variant] || meta.approve;
  const requireNote = variant === 'reject' || variant === 'send_back';
  return (
    <>
      <Button size="sm" variant={variant === 'send_back' ? 'outline' : 'default'} className={m.cls} onClick={() => setOpen(true)} data-testid={testid}>
        {m.icon} {m.label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{m.label}?</DialogTitle>
            <DialogDescription>{requireNote ? 'A note is required so the requester understands the decision.' : 'Add an optional note to explain your action.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Note {requireNote && <span className="text-rose-600">*</span>}</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context (visible to the requester)" data-testid={`${testid}-note`} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={() => { onConfirm(note); setOpen(false); setNote(''); }}
              disabled={requireNote && !note.trim()}
              data-testid={`${testid}-confirm`}
              className={m.cls}
            >
              Confirm {m.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
